import React, { useState, useCallback } from 'react';
import axios from 'axios';
import useAuthStore from '../../store/authStore';

const PAY_STATUS_STYLE = {
  PAID: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  PENDING: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  PARTIAL: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
};

function Row({ label, value, bold, highlight }) {
  return (
    <tr style={{ backgroundColor: highlight ? '#f0fdf4' : undefined }}>
      <td style={{ padding: '10px 16px', fontSize: 14, color: '#374151', fontWeight: bold ? 700 : 400 }}>{label}</td>
      <td style={{ padding: '10px 16px', fontSize: 14, textAlign: 'right', fontWeight: bold ? 700 : 400, color: highlight ? '#166534' : '#111' }}>
        PKR {parseFloat(value || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
      </td>
    </tr>
  );
}

export default function OPDBilling() {
  const token = useAuthStore(s => s.token);
  const headers = { Authorization: `Bearer ${token}` };

  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discount, setDiscount] = useState('0');
  const [discountReason, setDiscountReason] = useState('');
  const [discountApprover, setDiscountApprover] = useState('');
  const [otherCharges, setOtherCharges] = useState('0');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [policyNo, setPolicyNo] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [msg, setMsg] = useState('');

  const search = async () => {
    if (!searchQ.trim()) return;
    try {
      const res = await axios.get('/api/opd-billing/search', { headers, params: { q: searchQ } });
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
  };

  const loadVisit = async (visit) => {
    setSelectedVisit(visit);
    setLoading(true);
    setMsg('');
    setDiscount('0'); setDiscountReason(''); setDiscountApprover('');
    setOtherCharges('0');
    try {
      const res = await axios.get(`/api/opd-billing/calculate/${visit.id}`, { headers });
      setBill(res.data);
    } catch { setBill(null); }
    setLoading(false);
  };

  const discountAmt = bill ? (parseFloat(bill.subtotal || 0) * parseFloat(discount || 0)) / 100 : 0;
  const afterDiscount = bill ? parseFloat(bill.subtotal || 0) + parseFloat(otherCharges || 0) - discountAmt : 0;
  const balanceDue = bill ? Math.max(0, afterDiscount - parseFloat(bill.advance_paid || 0)) : 0;

  const saveBill = async (markPaid) => {
    if (!selectedVisit || !bill) return;
    if (parseFloat(discount) > 0 && !discountReason.trim()) { setMsg('Discount reason is required.'); return; }
    setSaving(true);
    try {
      await axios.post('/api/opd-billing/save', {
        visit_id: selectedVisit.id,
        discount: parseFloat(discount) || 0,
        discount_reason: discountReason,
        discount_approved_by: discountApprover,
        other_charges: parseFloat(otherCharges) || 0,
        balance_due: balanceDue,
        payment_status: markPaid ? 'PAID' : balanceDue <= 0 ? 'PAID' : bill.advance_paid > 0 ? 'PARTIAL' : 'PENDING',
        payment_mode: paymentMode,
        policy_no: policyNo,
        auth_code: authCode,
      }, { headers });
      setMsg(markPaid ? 'Bill marked as PAID.' : 'Bill saved.');
      loadVisit(selectedVisit);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Failed to save bill.');
    }
    setSaving(false);
  };

  const printReceipt = () => {
    if (!selectedVisit || !bill) return;
    const content = `<html><head><title>OPD Receipt</title>
    <style>body{font-family:Arial;padding:20px;font-size:13px}h2{margin:0 0 4px}table{width:100%;border-collapse:collapse}td{padding:6px 10px;border-bottom:1px solid #f0f0f0}td:last-child{text-align:right}.total{font-weight:bold;border-top:2px solid #111}</style></head><body>
    <h2>OPD Receipt</h2>
    <p>Visit: ${selectedVisit.visit_number || '-'} &nbsp;|&nbsp; ${new Date(selectedVisit.created_at).toLocaleDateString('en-PK')}</p>
    <p><strong>Patient:</strong> ${selectedVisit.patient_name} (${selectedVisit.mrn}) &nbsp;|&nbsp; Dr. ${selectedVisit.doctor || '-'}</p><hr/>
    <table>
      <tr><td>Consultation Fee</td><td>PKR ${parseFloat(bill.visit_fee || 0).toFixed(2)}</td></tr>
      <tr><td>Lab Charges</td><td>PKR ${parseFloat(bill.lab_charges || 0).toFixed(2)}</td></tr>
      <tr><td>Radiology Charges</td><td>PKR ${parseFloat(bill.radiology_charges || 0).toFixed(2)}</td></tr>
      <tr><td>Pharmacy Charges</td><td>PKR ${parseFloat(bill.pharmacy_charges || 0).toFixed(2)}</td></tr>
      ${parseFloat(otherCharges) > 0 ? `<tr><td>Other Charges</td><td>PKR ${parseFloat(otherCharges).toFixed(2)}</td></tr>` : ''}
      ${parseFloat(discount) > 0 ? `<tr><td>Discount (${discount}%)</td><td>- PKR ${discountAmt.toFixed(2)}</td></tr>` : ''}
      <tr class="total"><td>Total</td><td>PKR ${afterDiscount.toFixed(2)}</td></tr>
      <tr><td>Advance Paid</td><td>PKR ${parseFloat(bill.advance_paid || 0).toFixed(2)}</td></tr>
      <tr class="total"><td>Balance Due</td><td>PKR ${balanceDue.toFixed(2)}</td></tr>
    </table>
    <br/><p style="font-size:11px;text-align:center">Thank you for visiting</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=500,height=600');
    if (w) { w.document.open(); w.document.write(content); w.document.close(); w.print(); }
  };

  const ps = selectedVisit?.payment_status ? PAY_STATUS_STYLE[selectedVisit.payment_status] || PAY_STATUS_STYLE.PENDING : null;

  return (
    <div style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>OPD Billing</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>Search visits and manage billing</p>
      </div>

      {/* Search */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="Search by visit number, MRN, or patient name..."
            style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }} />
          <button onClick={search} style={{ padding: '10px 24px', border: 'none', borderRadius: 8, backgroundColor: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Search</button>
        </div>

        {searchResults.length > 0 && (
          <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            {searchResults.map(v => {
              const ps2 = v.payment_status ? PAY_STATUS_STYLE[v.payment_status] || PAY_STATUS_STYLE.PENDING : PAY_STATUS_STYLE.PENDING;
              return (
                <div key={v.id} onClick={() => loadVisit(v)} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{v.patient_name} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({v.mrn})</span></div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{v.visit_number} · {new Date(v.created_at).toLocaleDateString('en-PK')} · Dr. {v.doctor || '-'}</div>
                  </div>
                  <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: ps2.bg, color: ps2.color, border: `1px solid ${ps2.border}` }}>
                    {v.payment_status || 'PENDING'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Calculating bill...</div>}

      {selectedVisit && bill && !loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20 }}>
          {/* Bill breakdown */}
          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {/* Patient info header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{selectedVisit.patient_name}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                  MRN: {selectedVisit.mrn} &nbsp;|&nbsp; Dr. {selectedVisit.doctor || '-'} &nbsp;|&nbsp; {new Date(selectedVisit.created_at).toLocaleDateString('en-PK')}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Visit: {selectedVisit.visit_number || '-'} &nbsp;|&nbsp; {selectedVisit.payment_type}</div>
              </div>
              {ps && <span style={{ padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, backgroundColor: ps.bg, color: ps.color, border: `1px solid ${ps.border}` }}>{selectedVisit.payment_status || 'PENDING'}</span>}
            </div>

            {/* Bill table */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <Row label="Consultation Fee" value={bill.visit_fee} />
                <Row label="Lab Charges" value={bill.lab_charges} />
                <Row label="Radiology Charges" value={bill.radiology_charges} />
                <Row label="Pharmacy Charges" value={bill.pharmacy_charges} />
                <tr>
                  <td style={{ padding: '10px 16px', fontSize: 14 }}>Other Charges</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <input type="number" min="0" value={otherCharges} onChange={e => setOtherCharges(e.target.value)}
                      style={{ width: 120, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, textAlign: 'right' }} />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                  <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 700 }}>Subtotal</td>
                  <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 700, textAlign: 'right' }}>
                    PKR {(parseFloat(bill.subtotal || 0) + parseFloat(otherCharges || 0)).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '10px 16px', fontSize: 14 }}>
                    Discount %
                    {parseFloat(discount) > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <input type="text" placeholder="Reason *" value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                          style={{ width: '90%', padding: '4px 8px', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 12, display: 'block', marginBottom: 4 }} />
                        <input type="text" placeholder="Approved by" value={discountApprover} onChange={e => setDiscountApprover(e.target.value)}
                          style={{ width: '90%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, display: 'block' }} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <input type="number" min="0" max="100" value={discount} onChange={e => setDiscount(e.target.value)}
                        style={{ width: 80, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, textAlign: 'right' }} />
                      <span style={{ fontSize: 13, color: '#6b7280' }}>%</span>
                    </div>
                    {discountAmt > 0 && <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>- PKR {discountAmt.toFixed(2)}</div>}
                  </td>
                </tr>
                <Row label="After Discount" value={afterDiscount} bold />
                <Row label="Advance Paid" value={bill.advance_paid} />
                <Row label="Balance Due" value={balanceDue} bold highlight />
              </tbody>
            </table>
          </div>

          {/* Right panel — payment & actions */}
          <div>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Payment Details</div>

              {selectedVisit.payment_type === 'Cash' ? (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 6 }}>Payment Mode</label>
                  {['Cash', 'Card', 'Bank Transfer'].map(m => (
                    <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                      <input type="radio" name="mode" value={m} checked={paymentMode === m} onChange={() => setPaymentMode(m)} />
                      <span style={{ fontSize: 14 }}>{m}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Policy Number</label>
                    <input type="text" value={policyNo} onChange={e => setPolicyNo(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Auth Code</label>
                    <input type="text" value={authCode} onChange={e => setAuthCode(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}
            </div>

            {msg && <div style={{ padding: '10px 14px', backgroundColor: msg.includes('Failed') ? '#fef2f2' : '#f0fdf4', border: `1px solid ${msg.includes('Failed') ? '#fecaca' : '#bbf7d0'}`, borderRadius: 8, fontSize: 13, color: msg.includes('Failed') ? '#991b1b' : '#166534', marginBottom: 14 }}>{msg}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => saveBill(true)} disabled={saving} style={{ padding: '12px 0', border: 'none', borderRadius: 8, backgroundColor: saving ? '#6ee7b7' : '#16a34a', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 700 }}>
                Mark as PAID
              </button>
              <button onClick={() => saveBill(false)} disabled={saving} style={{ padding: '10px 0', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                Save Bill
              </button>
              <button onClick={printReceipt} style={{ padding: '10px 0', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
